import { Input, InputNumber, Select, Space, Typography } from 'antd';
import type { ReportColumn } from '@goelectric/shared-types';
import {
  opcionTodos,
  TODOS,
  type ReportFilterValues,
  type ReportRangeValue,
} from '../utils/format';
import type { CatalogosOrigenState } from '../hooks/useCatalogoOrigenes';

export interface ReportFiltersFormProps {
  /**
   * Columnas activas que declaran `filtro`, en el orden declarado. La
   * filtrabilidad es una propiedad de la columna: no hay lista aparte de filtros.
   */
  columnas: ReportColumn[];
  values: ReportFilterValues;
  onChange: (values: ReportFilterValues) => void;
  /** Catálogos por `origen` ya cargados (o en carga/error). */
  catalogos?: CatalogosOrigenState;
  disabled?: boolean;
}

/**
 * Formulario generado desde las columnas filtrables activas. La clave de cada
 * control es la `clave` de la columna.
 */
export function ReportFiltersForm({
  columnas,
  values,
  onChange,
  catalogos,
  disabled,
}: ReportFiltersFormProps) {
  const set = (clave: string, value: unknown) => onChange({ ...values, [clave]: value });

  return (
    <div className="reportes-form">
      {columnas.length === 0 ? (
        <Typography.Text type="secondary">Este reporte no tiene filtros.</Typography.Text>
      ) : (
        columnas.map((columna) => (
          <div key={columna.clave} className="reportes-form__field">
            <span className="reportes-form__label">{columna.titulo}</span>
            {renderCampo(columna)}
          </div>
        ))
      )}
    </div>
  );

  function renderCampo(columna: ReportColumn) {
    const clave = columna.clave;
    const value = values[clave];
    const operador = columna.filtro?.operador;

    if (columna.tipo === 'catalogo') return renderCatalogo(columna, value);
    if (columna.tipo === 'fecha' && operador === 'rango') {
      return renderRangoFecha(columna, value);
    }
    if ((columna.tipo === 'numero' || columna.tipo === 'moneda') && operador === 'rango') {
      return renderRangoNumero(columna, value);
    }
    if (columna.tipo === 'fecha') {
      return (
        <Input
          type="date"
          disabled={disabled}
          aria-label={columna.titulo}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => set(clave, event.target.value)}
        />
      );
    }
    // Texto (`contiene`) y cualquier combinación no prevista: input simple.
    return (
      <Input
        allowClear
        disabled={disabled}
        aria-label={columna.titulo}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => set(clave, event.target.value)}
      />
    );
  }

  function renderCatalogo(columna: ReportColumn, value: unknown) {
    const estado = columna.origen ? catalogos?.estado[columna.origen] : undefined;
    // "Todos/Todas" siempre primero: elegirla quita la clave del filtro.
    const opciones = [
      opcionTodos(columna),
      ...(columna.origen ? (catalogos?.opciones[columna.origen] ?? []) : (columna.opciones ?? [])),
    ].map((opcion) => ({ value: opcion.valor, label: opcion.etiqueta }));

    if (columna.origen && estado === 'error') {
      return (
        <Space direction="vertical" size={2}>
          <Select disabled aria-label={columna.titulo} placeholder="No disponible" options={[]} />
          <Typography.Text type="danger" className="reportes-form__hint">
            No se pudieron cargar las opciones. Reintentá seleccionando el reporte.
          </Typography.Text>
        </Space>
      );
    }

    // Elegir "Todos/Todas" borra la clave: el sentinel nunca queda en el estado.
    const cambiar = (next: string) => {
      if (next !== TODOS) {
        set(columna.clave, next);
        return;
      }
      const resto = { ...values };
      delete resto[columna.clave];
      onChange(resto);
    };

    return (
      <Select
        showSearch={columna.origen !== undefined}
        optionFilterProp="label"
        loading={estado === 'cargando'}
        disabled={disabled}
        aria-label={columna.titulo}
        value={typeof value === 'string' ? value : TODOS}
        onChange={cambiar}
        options={opciones}
      />
    );
  }

  function renderRangoFecha(columna: ReportColumn, value: unknown) {
    const rango: ReportRangeValue =
      value && typeof value === 'object' ? (value as ReportRangeValue) : {};
    const setRango = (next: ReportRangeValue) => set(columna.clave, next);
    return (
      <Space.Compact block>
        <Input
          type="date"
          disabled={disabled}
          aria-label={`${columna.titulo} desde`}
          value={typeof rango.desde === 'string' ? rango.desde : ''}
          onChange={(event) => setRango({ ...rango, desde: event.target.value || undefined })}
        />
        <Input
          type="date"
          disabled={disabled}
          aria-label={`${columna.titulo} hasta`}
          value={typeof rango.hasta === 'string' ? rango.hasta : ''}
          onChange={(event) => setRango({ ...rango, hasta: event.target.value || undefined })}
        />
      </Space.Compact>
    );
  }

  function renderRangoNumero(columna: ReportColumn, value: unknown) {
    const rango: ReportRangeValue =
      value && typeof value === 'object' ? (value as ReportRangeValue) : {};
    const setRango = (next: ReportRangeValue) => set(columna.clave, next);
    return (
      <Space.Compact block>
        <InputNumber
          style={{ width: '100%' }}
          disabled={disabled}
          aria-label={`${columna.titulo} desde`}
          placeholder="Desde"
          value={typeof rango.desde === 'number' ? rango.desde : null}
          onChange={(next) => setRango({ ...rango, desde: next ?? undefined })}
        />
        <InputNumber
          style={{ width: '100%' }}
          disabled={disabled}
          aria-label={`${columna.titulo} hasta`}
          placeholder="Hasta"
          value={typeof rango.hasta === 'number' ? rango.hasta : null}
          onChange={(next) => setRango({ ...rango, hasta: next ?? undefined })}
        />
      </Space.Compact>
    );
  }
}
